import { Intent } from "@blueprintjs/core";
import {
  CellId,
  CellRunMode,
  ExploreCellId,
  ExploreSpec,
  ExploreSpecUpdateActions,
  HexVersionAtomicOperation,
  UPDATE_EXPLORE_CELL,
  exploreSpecReducer,
} from "@hex/common";
import { isEqual } from "lodash";
import { useCallback } from "react";

import { DispatchAOResult } from "../../atomic-operations";
import { useToaster } from "../../components/common/Toasts";
import { useLockStatus, useUpdateLock } from "../../hooks/locks/useLock.js";
import {
  useProjectVersionEditable,
  useSessionEditable,
} from "../../hooks/sessionOrProjectEditableHooks.js";
import { useSelector, useStore } from "../../redux/hooks";
import { appSessionMPSelectors } from "../../redux/slices/appSessionMPSlice";
import { savedViewDefaultAppSessionSelectors } from "../../redux/slices/savedViewDefaultAppSessionSlice.js";
import { useAppSessionAOContext } from "../../util/appSessionAOContext.js";
import { useCellContext } from "../../util/cellContext.js";
import { useHexVersionAOContext } from "../../util/hexVersionAOContext.js";
import { useSessionContext } from "../../util/sessionContext.js";
import { getUpdateExploreSpecSessionStateAOs } from "../utils/exploreSpecSessionStateUpdates.js";

import { useExploreCellContentsGetter } from "./exploreCellContentsStateHooks";
import { useExploreSpecSessionStateGetter } from "./exploreSpecSessionStateHooks.js";

export type OnUpdateExploreSpec = (
  actions: ExploreSpecUpdateActions | ExploreSpecUpdateActions[],
  options?: UpdateExploreSpecOptions,
) => void;

export interface UpdateExploreSpecOptions {
  suppressAutoRun?: boolean;
  additionalActions?: HexVersionAtomicOperation[];
  skipUndoBuffer?: boolean;
}

export function useUpdateExploreSpec(
  cellId: CellId,
): (
  actions: ExploreSpecUpdateActions | ExploreSpecUpdateActions[],
  options?: UpdateExploreSpecOptions,
) => DispatchAOResult | undefined {
  const projectVersionEditable = useProjectVersionEditable();
  const sessionEditable = useSessionEditable();
  const { isLogicSession } = useSessionContext();
  const { acquireLock } = useUpdateLock({
    cellId,
    canUnlock: projectVersionEditable ?? false,
  });

  const { dispatchAO: dispatchHexVersionAO } = useHexVersionAOContext();
  const { dispatchAO: dispatchAppSessionAO } = useAppSessionAOContext();
  const { appSessionId } = useSessionContext();
  const store = useStore();
  const appSessionCellId = useSelector(
    (state) =>
      appSessionMPSelectors
        .getAppSessionCellSelectors(appSessionId)
        .selectByCellId(state, cellId)?.id,
  );

  const getExploreSpec = useExploreCellContentsGetter({
    selector: (contents) => ({
      spec: contents.spec,
      exploreCellId: contents.exploreCellId,
    }),
  });

  const getExploreSpecSessionState = useExploreSpecSessionStateGetter();

  const toaster = useToaster();

  const updateExploreSpec: (
    actions: ExploreSpecUpdateActions | ExploreSpecUpdateActions[],
    options?: UpdateExploreSpecOptions,
  ) => DispatchAOResult | undefined = useCallback(
    (
      actions,
      {
        additionalActions = [],
        skipUndoBuffer = false,
        suppressAutoRun = false,
      } = {},
    ) => {
      acquireLock();
      const { exploreCellId, spec: origSpec } = getExploreSpec(cellId);

      if (isLogicSession && projectVersionEditable) {
        const ops = getUpdateExploreSpecAOs({
          actions,
          cellId,
          exploreCellId,
          origSpec,
          suppressAutoRun,
        });

        if (additionalActions.length > 0) {
          ops.push(...additionalActions);
        }

        if (ops.length > 0) {
          return dispatchHexVersionAO(ops, { skipUndoBuffer });
        }
      } else if (!isLogicSession && sessionEditable) {
        if (!appSessionCellId) {
          toaster.show({
            message:
              "There was an error updating the visualization. Please contact support if the issue persists.",
            intent: Intent.DANGER,
          });
          console.error(
            "appSessionCellId not found when attempting to update explore spec session state for cell: ",
            cellId,
          );
          return undefined;
        }

        const sessionOps = getUpdateExploreSpecSessionStateAOs({
          actions,
          appSessionCellId,
          exploreCellId,
          getExploreSpecSessionState,
          currentSpec: origSpec,
          defaultFieldOverrides:
            savedViewDefaultAppSessionSelectors
              .selectExploreSpecSessionStates(store.getState())
              .find((state) => state.exploreCellId === exploreCellId)
              ?.fieldOverrides ?? null,
        });

        if (sessionOps.length > 0) {
          return dispatchAppSessionAO(sessionOps);
        }
      }
    },
    [
      acquireLock,
      appSessionCellId,
      cellId,
      dispatchHexVersionAO,
      dispatchAppSessionAO,
      getExploreSpec,
      isLogicSession,
      projectVersionEditable,
      sessionEditable,
      getExploreSpecSessionState,
      store,
      toaster,
    ],
  );

  const lockStatus = useLockStatus(cellId);
  const inImportedComponent = useCellContext((c) => c.inImportedComponent);
  const editableAndUnlocked =
    lockStatus !== "other-user-locked" &&
    (projectVersionEditable || sessionEditable) &&
    !inImportedComponent;

  // Not 100% necessary, but avoids HTTP requests if someone edits the webpage
  // and tries to edit a readonly explore cell
  if (!editableAndUnlocked) {
    return doNothing;
  }

  return updateExploreSpec;
}

const doNothing = () => undefined;

/**
 * Return the AOs needed to apply the explore spec update `actions`.
 *
 * Really meant for `updateExploreSpec` but can be used to include AOs
 * in a single dispatch with other AOs (e.g. updating display table config).
 */
export function getUpdateExploreSpecAOs({
  actions,
  cellId,
  cellRunMode,
  exploreCellId,
  origSpec,
  suppressAutoRun,
}: {
  cellId: CellId;
  actions: ExploreSpecUpdateActions | ExploreSpecUpdateActions[];
  origSpec: ExploreSpec;
  exploreCellId: ExploreCellId;
  suppressAutoRun: boolean;
  cellRunMode?: CellRunMode;
}) {
  const actionsArray = Array.isArray(actions) ? actions : [actions];
  const newSpec = actionsArray.reduce(
    (prevSpec, action) => exploreSpecReducer(prevSpec, action),
    origSpec,
  );

  const ops: HexVersionAtomicOperation[] = [];
  if (!isEqual(origSpec, newSpec)) {
    ops.push(
      UPDATE_EXPLORE_CELL.create({
        cellId,
        exploreCellId,
        key: "spec",
        value: newSpec,
        suppressAutoRun,
        cellRunMode,
      }),
    );
  }

  return ops;
}
